import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { apiClient } from "../utils/API/apiClient";

interface Message {
  text: string;
  sender: "user" | "bot";
  time: string;
}

/* ---------------------------
   SUB-COMPONENTS
---------------------------- */

const ChatHeader: React.FC<{ isOnline: boolean; onClose: () => void }> = ({
  isOnline,
  onClose,
}) => (
  <div style={styles.header}>
    <span>{isOnline ? "🟢 Online" : "🔴 Offline"}</span>
    <button onClick={onClose} style={styles.closeBtn}>
      ✖
    </button>
  </div>
);

const MessageBubble: React.FC<{ msg: Message }> = ({ msg }) => (
  <div
    style={{
      marginBottom: "10px",
      textAlign: msg.sender === "user" ? "right" : "left",
    }}
  >
    <div
      style={{
        display: "inline-block",
        padding: "8px 12px",
        borderRadius: "12px",
        background: msg.sender === "user" ? "#4f46e5" : "#f1f1f1",
        color: msg.sender === "user" ? "#fff" : "#333",
        maxWidth: "80%",
      }}
    >
      {msg.text}
    </div>
    <div style={{ fontSize: "10px", color: "#888" }}>{msg.time}</div>
  </div>
);

const OptionButton: React.FC<{
  label: string;
  onClick: () => void;
  style?: React.CSSProperties;
}> = ({ label, onClick, style }) => (
  <div
    onClick={onClick}
    style={{
      padding: "6px 15px",
      borderRadius: "12px",
      background: "#ffffff",
      border: "1px solid #e2e8f0",
      cursor: "pointer",
      fontSize: "12px",
      fontWeight: 500,
      boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
      transition: "all 0.2s ease",
      ...style,
    }}
    onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")}
    onMouseLeave={(e) => (e.currentTarget.style.background = "#ffffff")}
  >
    {label}
  </div>
);

const MenuContainer: React.FC<{ 
  children?: React.ReactNode; 
  options?: string[]; 
  onOptionClick?: (label: string) => void;
}> = ({ children, options, onOptionClick }) => (
  <div style={styles.menuGrid}>
    {options && onOptionClick
      ? options.map((opt, i) => (
          <OptionButton key={i} label={opt} onClick={() => onOptionClick(opt)} />
        ))
      : children}
  </div>
);

const EmailPrompt: React.FC<{
  email: string;
  onChange: (val: string) => void;
  onSubmit: () => void;
}> = ({ email, onChange, onSubmit }) => (
  <div style={{ marginTop: "10px" }}>
    <input
      type="email"
      placeholder="Enter your email"
      value={email}
      onChange={(e) => onChange(e.target.value)}
      style={styles.emailInput}
    />
    <button onClick={onSubmit} style={styles.sendBtn}>
      Submit
    </button>
  </div>
);

const ChatInput: React.FC<{
  value: string;
  onChange: (val: string) => void;
  onSend: () => void;
}> = ({ value, onChange, onSend }) => (
  <div style={styles.inputArea}>
    <input
      type="text"
      placeholder="Type your message..."
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={styles.input}
      onKeyDown={(e) => e.key === "Enter" && onSend()}
    />
    <button onClick={onSend} style={styles.sendBtn}>
      Send
    </button>
  </div>
);

const ChatWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [askEmail, setAskEmail] = useState(false);
  const [email, setEmail] = useState("");
  const [emailCaptured, setEmailCaptured] = useState(false);
  const [showTopics, setShowTopics] = useState(false);
  const [showServiceMenu, setShowServiceMenu] = useState(false);
  const [showIntegrationMenu, setShowIntegrationMenu] = useState(false);
  const [showConsentMenu, setShowConsentMenu] = useState(false);
  const [leadId, setLeadId] = useState<string | null>(null);

  const { user } = useAuth();

  const integrationOptions = [
    "Salesforce",
    "Slack",
    "HubSpot",
    "Zapier",
    "Custom API",
  ];

  const serviceOptions = [
    "Automate Business",
    "Social Media Growth",
    "Mobile Apps",
    "Website Development",
    "Tools & Utilities",
    "Integrations",
  ];

  const quickTopics = [
    "Facebook Integration",
    "Instagram Integration",
    "YouTube Automation",
    "App Analytics",
    "Pricing Plans",
    "Technical Support",
    "Service Request",
  ];

  /* ---------------------------
     ONLINE STATUS
  ---------------------------- */

  useEffect(() => {
    const updateStatus = () => setIsOnline(navigator.onLine);
    window.addEventListener("online", updateStatus);
    window.addEventListener("offline", updateStatus);
    return () => {
      window.removeEventListener("online", updateStatus);
      window.removeEventListener("offline", updateStatus);
    };
  }, []);

  /* ---------------------------
     INITIAL BOT MESSAGE
  ---------------------------- */

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const greeting = user
        ? `Hi ${user.name} 👋 How can we help you?`
        : "Hi 👋 How can we help you?";
      addBotMessage(greeting);

      // Trigger Identity Step immediately after greeting
      setTimeout(() => {
        triggerIdentityFlow();
      }, 600);
    }
  }, [isOpen, user]);

  /* ---------------------------
     DETECT LOGIN TRANSITION
  ---------------------------- */
  const prevUser = useRef(user);

  useEffect(() => {
    // Detect transition from not logged-in to logged-in
    if (!prevUser.current && user && isOpen) {
      addBotMessage(
        `Welcome back, ${user.name}! I've updated your session to your account context.`
      );

      // Successfully transition from any anonymous stage
      setAskEmail(false);
      setEmailCaptured(true);
      setEmail(user.email); // Sync local email state to user account

      // Optional: Auto-trigger topics if they weren't already visible and we were stuck at email prompt
      if (!showTopics && !showServiceMenu && !showIntegrationMenu) {
        setShowTopics(true);
      }

      // Capture the transition in backend
      apiClient("/api/user/support-request", {
        method: "POST",
        body: {
          email: user.email,
          topic: "User Login Detection",
          message: `User logged in during chat session. Previous manual email (if any): ${email || "None"}`,
          consent: true,
          leadId,
        },
        apiType: 2,
      }).catch((err) => console.error("Failed to sync login event:", err));
    }
    prevUser.current = user;
  }, [user, isOpen]);

  /* ---------------------------
     HELPERS
  ---------------------------- */

  const getTime = () => {
    const now = new Date();
    return now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const addBotMessage = (text: string) => {
    setMessages((prev) => [...prev, { text, sender: "bot", time: getTime() }]);
  };

  const triggerIdentityFlow = () => {
    if (user) {
      addBotMessage(
        `Would you like us to send this chat summary to your registered email: ${user.email}?`
      );
      setShowConsentMenu(true);
    } else {
      addBotMessage("Please provide your email to help us reach you:");
      setAskEmail(true);
    }
  };

  const handleSend = () => {
    if (!input.trim()) return;

    const userMsg: Message = {
      text: input,
      sender: "user",
      time: getTime(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput("");

    // Simple auto-reply
    setTimeout(() => {
      addBotMessage("Thanks! Our team will get back to you shortly.");
    }, 600);

    // Send manual message to backend
    apiClient("/api/user/support-request", {
      method: "POST",
      body: {
        email: user?.email || email,
        topic: "Manual Message",
        message: input,
        consent: true,
      },
      apiType: 2,
    }).catch((err) => console.error("Failed to send manual message:", err));
  };

  const handleEmailSubmit = () => {
    if (!email.includes("@")) return;

    setEmailCaptured(true);
    addBotMessage("Thanks! Now, how can I assist you today?");
    setAskEmail(false);
    setShowTopics(true);

    // Send to backend
    apiClient("/api/user/capture-lead", {
      method: "POST",
      body: { email },
      apiType: 2,
    })
      .then((res: any) => {
        if (res.data?.id) setLeadId(res.data.id);
      })
      .catch((err) => console.error("Failed to capture lead:", err));
  };
  const handleTopicClick = (label: string, type: "topic" | "service" | "integration" = "topic") => {
    setInput(label);

    // 1. Update UI messages
    setTimeout(() => {
      setMessages((prev) => [...prev, { text: label, sender: "user", time: getTime() }]);
      
      // 2. Determine next steps based on type and label
      if (type === "topic") {
        setShowTopics(false);
        if (label === "Service Request") {
          setTimeout(() => {
            addBotMessage("For what feature would you like to request a service?");
            setShowServiceMenu(true);
          }, 600);
        } else {
          setTimeout(() => addBotMessage("Great choice! Let me help you with that."), 600);
        }
      } else if (type === "service") {
        setShowServiceMenu(false);
        if (label === "Integrations") {
          setTimeout(() => {
            addBotMessage("Which specific integration are you looking for?");
            setShowIntegrationMenu(true);
          }, 600);
        } else {
          setTimeout(() => addBotMessage("Got it! Our team will get back to you shortly."), 600);
        }
      } else if (type === "integration") {
        setShowIntegrationMenu(false);
        setTimeout(() => addBotMessage("Great! I've noted down your integration preference."), 600);
      }
    }, 200);

    // 3. Send to backend with category-specific topic
    const apiTopic = type === "topic" ? "Quick Topic Selection" : 
                    type === "service" ? "Service Request" : "Integration Request";

    apiClient("/api/user/support-request", {
      method: "POST",
      body: {
        email: user?.email || email,
        topic: apiTopic,
        message: `User selected ${type}: ${label}`,
        consent: true,
      },
      apiType: 2,
    }).catch((err) => console.error(`Failed to send ${type} selection:`, err));
  };

  const handleConsentClick = (consent: boolean) => {
    setMessages((prev) => [
      ...prev,
      { text: consent ? "Yes" : "No", sender: "user", time: getTime() },
    ]);
    setShowConsentMenu(false);

    setTimeout(() => {
      if (consent) {
        addBotMessage(
          `Thanks! We'll send the summary to ${user?.email} later. Now, how can I assist you today?`
        );
      } else {
        addBotMessage("No problem! How can I assist you today?");
      }
      setShowTopics(true);
    }, 600);

    // Send consent to backend
    apiClient("/api/user/support-request", {
      method: "POST",
      body: {
        email: user?.email || email,
        topic: "Chat Summary Consent",
        message: consent
          ? "User consented to chat summary"
          : "User declined chat summary",
        consent,
      },
      apiType: 2,
    }).catch((err) => console.error("Failed to send consent:", err));
  };

  return (
    <>
      {/* CHAT WINDOW */}
      {isOpen && (
        <div style={styles.chatBox}>
          <ChatHeader isOnline={isOnline} onClose={() => setIsOpen(false)} />

          <div style={styles.body}>
            {messages.map((msg, index) => (
              <MessageBubble key={index} msg={msg} />
            ))}

            {/* IDENTITY CONSENT */}
            {showConsentMenu && (
              <MenuContainer>
                <OptionButton label="Yes" onClick={() => handleConsentClick(true)} />
                <OptionButton label="No" onClick={() => handleConsentClick(false)} />
              </MenuContainer>
            )}

            {/* EMAIL PROMPT */}
            {askEmail && !emailCaptured && (
              <EmailPrompt
                email={email}
                onChange={setEmail}
                onSubmit={handleEmailSubmit}
              />
            )}

            {/* QUICK TOPICS */}
            {showTopics && (
              <MenuContainer
                options={quickTopics}
                onOptionClick={(label) => handleTopicClick(label, "topic")}
              />
            )}

            {/* SERVICE OPTIONS MENU */}
            {showServiceMenu && (
              <MenuContainer
                options={serviceOptions}
                onOptionClick={(label) => handleTopicClick(label, "service")}
              />
            )}

            {/* INTEGRATION OPTIONS MENU */}
            {showIntegrationMenu && (
              <MenuContainer
                options={integrationOptions}
                onOptionClick={(label) => handleTopicClick(label, "integration")}
              />
            )}
          </div>

          <ChatInput value={input} onChange={setInput} onSend={handleSend} />
        </div>
      )}

      {/* FLOATING BUTTON */}
      <div style={styles.floatingBtn} onClick={() => setIsOpen(!isOpen)}>
        💬
      </div>
    </>
  );
};

export default ChatWidget;

/* ---------------------------
   STYLES
---------------------------- */

const styles: { [key: string]: React.CSSProperties } = {
  floatingBtn: {
    position: "fixed",
    bottom: "20px",
    right: "20px",
    width: "60px",
    height: "60px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #714B67, #017E84)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px",
    cursor: "pointer",
    boxShadow: "0 8px 20px rgba(113,75,103,0.4)",
    zIndex: 1000,
  },

  chatBox: {
    position: "fixed",
    bottom: "90px",
    right: "20px",
    width: "340px",
    height: "450px",
    backgroundColor: "#ffffff",
    borderRadius: "20px",
    boxShadow: "0 20px 50px rgba(0,0,0,0.15)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    zIndex: 1000,
  },

  header: {
    background: "linear-gradient(90deg, #714B67, #017E84)",
    color: "#fff",
    padding: "14px 16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontWeight: 600,
    fontSize: "14px",
  },

  closeBtn: {
    background: "rgba(255,255,255,0.2)",
    border: "none",
    color: "#fff",
    borderRadius: "6px",
    padding: "2px 6px",
    cursor: "pointer",
  },

  body: {
    flex: 1,
    padding: "14px",
    overflowY: "auto",
    fontSize: "14px",
    backgroundColor: "#f8f9ff",
  },
  menuGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginTop: "10px",
  },

  inputArea: {
    display: "flex",
    borderTop: "1px solid #eee",
    backgroundColor: "#fff",
  },

  input: {
    flex: 1,
    padding: "12px",
    border: "none",
    outline: "none",
    fontSize: "14px",
  },

  sendBtn: {
    backgroundColor: "#714B67",
    color: "#fff",
    border: "none",
    padding: "12px 16px",
    cursor: "pointer",
    fontWeight: 600,
  },

  emailInput: {
    width: "100%",
    padding: "10px",
    marginBottom: "8px",
    borderRadius: "8px",
    border: "1px solid #ddd",
  },
};
